import { Toaster as SonnerToaster, type ToasterProps } from 'sonner';

import { cn } from '@/lib/cn';
import { buttonVariants } from './button.variants';

/** Secondary `sm` kit button, shared by the toast's action and cancel buttons. */
const TOAST_BUTTON = cn(buttonVariants({ variant: 'secondary', size: 'sm' }));

/**
 * The one toast look of the tool UI: a floating card (`rounded-card`,
 * `shadow-e3`) whose background is tinted by type with the `*-subtle` tokens,
 * so success / error / warning / info read at a glance. Colors come from
 * tokens, so a Toaster mounted inside the editor's `.dark` root renders dark
 * and the global one renders light.
 *
 * The tint goes through `data-[type=…]` variants on the one `toast` class:
 * sonner applies `classNames.default` to EVERY toast and then the per-type
 * class on top, so two background utilities would fight on stylesheet order.
 * Variant utilities always come after the base one, so the neutral surface is
 * the fallback (plain `toast()`, loading) and the type tint wins over it.
 */
const TOAST_OPTIONS: ToasterProps['toastOptions'] = {
  unstyled: true,
  classNames: {
    toast: [
      'group font-sans rounded-card pointer-events-auto flex w-fit! max-w-[356px] items-center gap-2.5 border py-2 pl-3 pr-2 text-sm text-ink shadow-e3',
      'border-line bg-surface',
      'data-[type=success]:border-success/30 data-[type=success]:bg-success-subtle',
      'data-[type=error]:border-danger/30 data-[type=error]:bg-danger-subtle',
      'data-[type=warning]:border-warning/30 data-[type=warning]:bg-warning-subtle',
      'data-[type=info]:border-brand/30 data-[type=info]:bg-brand-subtle',
    ].join(' '),
    content: 'flex-1',
    title: 'font-medium leading-snug',
    description: 'text-ink-2 text-xs',
    icon: 'relative m-0! flex! size-4! shrink-0 items-center justify-center [&>svg]:size-4 group-data-[type=success]:text-success group-data-[type=error]:text-danger group-data-[type=warning]:text-warning group-data-[type=info]:text-brand',
    actionButton: `${TOAST_BUTTON} ml-1`,
    cancelButton: TOAST_BUTTON,
  },
};

/**
 * Sonner's Toaster with the kit look. Mounted twice on purpose: globally in
 * main.tsx (top-right of the page) and inside the panorama editor's viewport
 * (see `lib/editor-toast.ts`). Only the placement differs, never the look.
 */
export function Toaster(props: Omit<ToasterProps, 'toastOptions' | 'richColors'>) {
  return <SonnerToaster toastOptions={TOAST_OPTIONS} {...props} />;
}
